import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EmptyState, ErrorState, ExternalIcon, LoadingState, Modal, Pill, TrashIcon, useToast } from "@signet/ui";
import { compact } from "@signet/web-kit";
import { MIN_ACCOUNTS, TOO_EARLY_DAYS, type VariableResult } from "../../shared/results.ts";
import type { SupplierResult } from "../../shared/suppliers.ts";
import { addBios, fetchBios, fetchResults, removeBio, shortDate, stockKeys, TYPE_LABEL, type AccountType } from "./api.ts";
import { TypeBadge } from "./cards.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   The panels under the accounts: suppliers, best and worst setups, and the
   bios new accounts are given.
   ────────────────────────────────────────────────────────────────────────── */

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

function ResultTable({ result }: { result: VariableResult }) {
  return (
    <div className="igs-result">
      <h3>{result.label}</h3>
      <div className="table" role="table" aria-label={result.label}>
        <div className="table-row head" role="row">
          <span role="columnheader">Group</span>
          <span role="columnheader" className="num">Accounts</span>
          <span role="columnheader" className="num">Still alive</span>
          <span role="columnheader" className="num" title="Median days since going on a phone">Days on</span>
          <span role="columnheader" className="num" title="Share of linked accounts whose bio link was flagged">Link flagged</span>
          <span role="columnheader" className="num">Followers a week</span>
          <span role="columnheader" className="num">Views a week</span>
        </div>
        {result.groups.map((g) => (
          <div key={g.key} role="row" className="table-row">
            <span role="cell" className="igs-result-name">
              <span className="truncate" title={g.label}>{g.label}</span>
              {result.best === g.key ? <Pill tone="good">Best</Pill> : result.worst === g.key ? <Pill tone="bad">Worst</Pill> : null}
            </span>
            <span role="cell" className="num tabular">{g.accounts}</span>
            <span role="cell" className="num tabular">{g.alivePct}%</span>
            <span role="cell" className="num tabular">{g.medianDays}</span>
            <span role="cell" className="num tabular">{g.flaggedPct === null ? <span className="faint">-</span> : `${g.flaggedPct}%`}</span>
            <span role="cell" className="num tabular">{g.followersPerWeek === null ? <span className="faint">-</span> : compact(g.followersPerWeek)}</span>
            <span role="cell" className="num tabular">{g.viewsPerWeek === null ? <span className="faint">-</span> : compact(g.viewsPerWeek)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const faintZero = (n: number) => (n ? n : <span className="faint">0</span>);

/**
 * Each listing bought from, newest first (the boss, 2026-10-28, buying aged:
 * "does this show anything about the recent suppliers?"). The site alone was
 * no help: every account so far came through accsmarket.com.
 */
function SuppliersTable({ suppliers }: { suppliers: SupplierResult[] }) {
  const counts = { aged: suppliers.filter((s) => s.accountType === "aged").length, soft_reg: suppliers.filter((s) => s.accountType === "soft_reg").length };
  const [type, setType] = useState<AccountType | "all">("all");
  const shown = type === "all" ? suppliers : suppliers.filter((s) => s.accountType === type);
  return (
    <div className="igs-result igs-suppliers">
      <div className="igs-free-head">
        <p>
          Every account bought, used or not. Banned counts bans still in appeal, and its share is out of the accounts used so far. A listing is its number on the supplier's site, so two listings with the same title stay apart.
        </p>
        <div className="igs-type-switch" role="tablist" aria-label="Account type">
          {(["all", "aged", "soft_reg"] as const).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={type === t} className={`igs-type-tab ${t === "aged" ? "aged" : t === "soft_reg" ? "soft" : "unmarked"}${type === t ? " on" : ""}`} onClick={() => setType(t)}>
              {t === "all" ? "All" : TYPE_LABEL[t]} <b>{t === "all" ? suppliers.length : counts[t]}</b>
            </button>
          ))}
        </div>
      </div>
      {shown.length ? (
        <div className="table" role="table" aria-label="Suppliers">
          <div className="table-row head" role="row">
            <span role="columnheader">Supplier</span>
            <span role="columnheader" className="num">Last bought</span>
            <span role="columnheader" className="num">Bought</span>
            <span role="columnheader" className="num" title="Taken onto a phone">On phones</span>
            <span role="columnheader" className="num">Still alive</span>
            <span role="columnheader" className="num" title="Banned or suspended, appeals still open included">Banned</span>
            <span role="columnheader" className="num" title="Free to take, never on a phone">Not used yet</span>
            <span role="columnheader" className="num" title="Bought, then deleted from the list">Deleted</span>
          </div>
          {shown.map((s) => (
            <div key={s.key} role="row" className="table-row">
              <span role="cell" className="igs-supplier-name">
                <span className="igs-supplier-top">
                  <a href={s.link} target="_blank" rel="noreferrer noopener" title={s.link}>
                    {s.name} <ExternalIcon size={12} />
                  </a>
                  <TypeBadge type={s.accountType} mini />
                </span>
                {s.detail ? <span className="faint truncate" title={s.detail}>{s.detail}</span> : null}
              </span>
              <span role="cell" className="num tabular" title={s.orders > 1 ? `${s.orders} orders, first on ${shortDate(s.firstBought)}` : undefined}>
                {shortDate(s.lastBought)}
              </span>
              <span role="cell" className="num tabular">{s.bought}</span>
              <span role="cell" className="num tabular">{faintZero(s.onPhones)}</span>
              <span role="cell" className="num tabular">{faintZero(s.alive)}</span>
              <span role="cell" className="num tabular">
                {s.banned ? (
                  <>
                    <span className="igs-danger">{s.banned}</span>
                    {s.bannedPct !== null ? <span className="faint"> {s.bannedPct}%</span> : null}
                  </>
                ) : (
                  <span className="faint">0</span>
                )}
              </span>
              <span role="cell" className="num tabular">{faintZero(s.notUsed)}</span>
              <span role="cell" className="num tabular">{s.deleted ? s.deleted : <span className="faint">-</span>}</span>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState title={type === "all" ? "No suppliers yet" : `No ${TYPE_LABEL[type].toLowerCase()} suppliers yet`} body="A supplier shows here once its file is uploaded with its listing link." />
      )}
    </div>
  );
}

export function ResultsPanel() {
  const results = useQuery({ queryKey: stockKeys.results, queryFn: fetchResults, staleTime: 60_000 });
  const r = results.data;
  return (
    <>
      <section className="panel section-gap">
        <div className="panel-head">
          <div>
            <h2>Suppliers</h2>
            <p>Each listing bought from, newest first: how many of its accounts are still alive and how many got banned.</p>
          </div>
        </div>
        <div className="panel-body">
          {results.isLoading ? (
            <LoadingState />
          ) : results.isError || !r ? (
            <ErrorState title="Couldn't load the suppliers" onRetry={() => results.refetch()} />
          ) : (
            <SuppliersTable suppliers={r.suppliers} />
          )}
        </div>
      </section>
      <section className="panel section-gap">
        <div className="panel-head">
          <div>
            <h2>Best and worst setups</h2>
            <p>
              Ranked on the share still alive, then fewer flagged links, then followers gained. A group needs {MIN_ACCOUNTS} accounts before it can be called best or worst. A ban still in appeal is left out until its appeal is settled.
            </p>
          </div>
        </div>
        <div className="panel-body">
          {results.isLoading ? (
            <LoadingState />
          ) : results.isError || !r ? (
            <ErrorState title="Couldn't load the results" onRetry={() => results.refetch()} />
          ) : r.accounts === 0 ? (
            <EmptyState title="No accounts on phones yet" body="Results start once posters take accounts onto their phones." />
          ) : (
            <>
              {r.tooEarly ? (
                <p className="igs-note">
                  Too early to read much into this: the oldest account has had {r.oldestDays} {r.oldestDays === 1 ? "day" : "days"}. Give it {TOO_EARLY_DAYS}.
                </p>
              ) : null}
              <ResultTable result={r.setups} />
              <div className="igs-results-grid">
                {r.variables.map((v) => (
                  <ResultTable key={v.variable} result={v} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

export function BiosModal({ open, onClose, manages }: { open: boolean; onClose: () => void; manages: boolean }) {
  const [draft, setDraft] = useState("");
  const client = useQueryClient();
  const toast = useToast();
  const bios = useQuery({ queryKey: stockKeys.bios, queryFn: fetchBios, enabled: open });
  const done = () => client.invalidateQueries({ queryKey: stockKeys.bios });
  const add = useMutation({
    mutationFn: () => addBios(draft.split(/\r?\n/)),
    onSuccess: (r) => {
      void done();
      setDraft("");
      toast.success(r.added ? `${r.added} ${r.added === 1 ? "bio" : "bios"} added` : "Nothing new to add", r.added ? undefined : "Those bios are already on the list.");
    },
    onError: (error) => toast.error("Couldn't add them", messageOf(error)),
  });
  const remove = useMutation({ mutationFn: removeBio, onSuccess: () => done(), onError: (error) => toast.error("Couldn't remove it", messageOf(error)) });
  const list = bios.data?.bios ?? [];

  return (
    <Modal open={open} onClose={onClose} size="wide" title="Bios" description="New accounts get the least used bio on this list. Removing one keeps it on the accounts that already have it.">
      <div className="igs-bios">
        {manages ? (
          <div className="igs-bios-add">
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={"One bio a line. Paste as many as you like.\nMy new account :)\nFresh start 🤍"} rows={4} />
            <button type="button" className="button brass compact" disabled={!draft.trim() || add.isPending} onClick={() => add.mutate()}>
              Add bios
            </button>
          </div>
        ) : null}
        <p className="faint igs-small">{list.length} bios</p>
        <ul className="igs-bio-list">
          {list.map((bio) => (
            <li key={bio.id}>
              <span>{bio.text}</span>
              <small className="faint tabular">{bio.used ? `used ${bio.used}x` : "unused"}</small>
              {manages ? (
                <button type="button" className="icon-button" aria-label={`Remove "${bio.text}"`} onClick={() => remove.mutate(bio.id)}>
                  <TrashIcon size={13} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
